import React, { useState } from 'react';
import axios from 'axios';
import './UserSettingsModal.css';

const API_URL = '/api';

function UserSettingsModal({ auth, isOpen, onClose, onUpdated }) {
  const isAdmin = (auth?.user?.role || '').toUpperCase() === 'ADMIN';

  const [inAppNotif, setInAppNotif] = useState(() => {
    return auth?.user?.in_app_notifications !== false;
  });
  const [emailNotif, setEmailNotif] = useState(() => {
    return auth?.user?.email_notifications !== false;
  });
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwError, setPwError] = useState('');

  const handleClose = () => {
    setStatusMessage('');
    setErrorMessage('');
    setPwSuccess('');
    setPwError('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPw(false);
    setShowNewPw(false);
    setShowConfirmPw(false);
    onClose();
  };

  if (!isOpen) return null;

  const handleToggle = async (key, value) => {
    const newInApp = key === 'in_app' ? value : inAppNotif;
    const newEmail = key === 'email' ? value : emailNotif;

    if (key === 'in_app') setInAppNotif(value);
    if (key === 'email') setEmailNotif(value);

    if (isAdmin) return;

    setErrorMessage('');
    try {
      const res = await axios.patch(
        `${API_URL}/user/preferences`,
        {
          in_app_notifications: newInApp,
          email_notifications: newEmail
        },
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      );

      if (onUpdated) {
        onUpdated(res.data);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to save settings');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPwError('Please fill in all password fields.');
      return;
    }

    if (newPassword.length < 8) {
      setPwError('Password must be at least 8 characters long.');
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      setPwError('Password must contain at least one uppercase letter.');
      return;
    }

    if (!/[^a-zA-Z0-9]/.test(newPassword)) {
      setPwError('Password must contain at least one special character.');
      return;
    }

    if (newPassword === currentPassword) {
      setPwError('New password cannot be the same as the current password.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPwError('New password and confirmation password do not match.');
      return;
    }

    setPwLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/user/change-password`,
        {
          currentPassword,
          newPassword,
          confirmPassword
        },
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      );

      setPwSuccess(res.data?.message || 'Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-card settings-modal-card">
        <div className="settings-modal-header">
          <h3 className="settings-modal-title">Settings</h3>
          <button type="button" className="modal-close-btn" onClick={handleClose} aria-label="Close">
            &times;
          </button>
        </div>

        {statusMessage && <div className="settings-alert settings-alert-success">{statusMessage}</div>}
        {errorMessage && <div className="settings-alert settings-alert-danger">{errorMessage}</div>}

        {!isAdmin ? (
          <div className="settings-section">
            <div className="settings-section-title">Notifications</div>
            <div className="settings-toggle-row">
              <span className="settings-toggle-label">In-App Notifications</span>
              <label className="settings-switch">
                <input
                  type="checkbox"
                  checked={inAppNotif}
                  onChange={(e) => handleToggle('in_app', e.target.checked)}
                />
                <span className="settings-slider round"></span>
              </label>
            </div>

            <div className="settings-toggle-row">
              <span className="settings-toggle-label">Email Notifications</span>
              <label className="settings-switch">
                <input
                  type="checkbox"
                  checked={emailNotif}
                  onChange={(e) => handleToggle('email', e.target.checked)}
                />
                <span className="settings-slider round"></span>
              </label>
            </div>
          </div>
        ) : null}

        {/* Change Password Section */}
        <div className="settings-section settings-password-section">
          <div className="settings-section-title">Change Password</div>

          {pwSuccess && <div className="settings-alert settings-alert-success">{pwSuccess}</div>}
          {pwError && <div className="settings-alert settings-alert-danger">{pwError}</div>}

          <form onSubmit={handlePasswordSubmit} className="settings-password-form">
            <div className="settings-input-group">
              <label className="settings-input-label" htmlFor="currentPassword">Current Password</label>
              <div className="settings-password-wrapper">
                <input
                  id="currentPassword"
                  type={showCurrentPw ? 'text' : 'password'}
                  className="settings-input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                  maxLength={128}
                  required
                />
                <button
                  type="button"
                  className="settings-pw-toggle-btn"
                  onClick={() => setShowCurrentPw(!showCurrentPw)}
                  tabIndex="-1"
                  aria-label={showCurrentPw ? 'Hide password' : 'Show password'}
                >
                  {showCurrentPw ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="settings-input-group">
              <label className="settings-input-label" htmlFor="newPassword">New Password</label>
              <div className="settings-password-wrapper">
                <input
                  id="newPassword"
                  type={showNewPw ? 'text' : 'password'}
                  className="settings-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 8 chars, 1 uppercase, 1 special"
                  autoComplete="new-password"
                  maxLength={128}
                  required
                />
                <button
                  type="button"
                  className="settings-pw-toggle-btn"
                  onClick={() => setShowNewPw(!showNewPw)}
                  tabIndex="-1"
                  aria-label={showNewPw ? 'Hide password' : 'Show password'}
                >
                  {showNewPw ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="settings-input-group">
              <label className="settings-input-label" htmlFor="confirmPassword">Confirm New Password</label>
              <div className="settings-password-wrapper">
                <input
                  id="confirmPassword"
                  type={showConfirmPw ? 'text' : 'password'}
                  className="settings-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat new password"
                  autoComplete="new-password"
                  maxLength={128}
                  required
                />
                <button
                  type="button"
                  className="settings-pw-toggle-btn"
                  onClick={() => setShowConfirmPw(!showConfirmPw)}
                  tabIndex="-1"
                  aria-label={showConfirmPw ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPw ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="settings-pw-actions">
              <button
                type="submit"
                className="button button-primary settings-pw-submit-btn"
                disabled={pwLoading || !currentPassword || !newPassword || !confirmPassword}
              >
                {pwLoading ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </form>
        </div>

        <div className="settings-pwa-tip">
          <div className="settings-pwa-title">Add To Home Screen</div>
          <p className="settings-pwa-text">
            For fast access and better experience, tap <strong>Share</strong> (iOS Safari) or <strong>Menu</strong> (Android Chrome) and select <strong>Install and create shortcut</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default UserSettingsModal;